import HTMLParsedElement from "html-parsed-element"
import toggleAttribute from "./toggleAttribute"
import { morphdomPreserveAttributes } from "web-ui/morphdom/preserve-attributes"

const DEFAULT_TOLERANCE = 1;

class ScrollIndicator extends HTMLParsedElement {
  constructor() {
    super()

    this.visibilityObserver = new IntersectionObserver(this.onVisibilityChange)
  }

  connectedCallback() {
    super.connectedCallback()

    this.visibilityObserver.observe(this)
  }

  parsedCallback() {
    const scrollElement = this.getScrollElement()

    if (scrollElement) {
      scrollElement.addEventListener("scroll", this.onScroll)
    }

    morphdomPreserveAttributes(this, ["content-below", "content-above", "class", "style"])
  }

  disconnectedCallback() {
    this.visibilityObserver.disconnect()
    this.stopPolling()
    this.visible = undefined
  }

  onVisibilityChange = (entries) => {
    const entry = entries[entries.length - 1]

    this.visible = entry.isIntersecting

    if (this.visible) {
      this.displayIndicators()
      this.startPolling()
    } else {
      this.stopPolling()
    }
  }

  startPolling() {
    if (this.interval) {
      return
    }

    this.interval = setInterval(
      () => this.displayIndicators(),
      150
    )
  }

  stopPolling() {
    clearInterval(this.interval)
    this.interval = null
  }

  onScroll = () => {
    // The scrolling attribute is added while indicators are being displayed or
    // hidden which allows them to animate only in response to scrolling
    toggleAttribute(this, "scrolling", true)
    this.displayIndicators()
    toggleAttribute(this, "scrolling", false)
  }

  displayIndicators() {
    const scrollElement = this.getScrollElement()

    if (!scrollElement) {
      toggleAttribute(this, "content-above", false)
      toggleAttribute(this, "content-below", false)
      return
    }

    const scrollTop = scrollElement.scrollTop
    const scrollHeight = scrollElement.scrollHeight
    const clientHeight = scrollElement.clientHeight
    const tolerance = this.tolerance

    const contentAbove = scrollTop > tolerance
    toggleAttribute(this, "content-above", contentAbove)

    const contentBelow = Math.abs(scrollHeight - clientHeight - scrollTop) >= tolerance
    toggleAttribute(this, "content-below", contentBelow)

    const scrollbarWidth = scrollElement.offsetWidth - scrollElement.clientWidth
    this.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`)
  }

  getScrollElement() {
    return this.children[0]
  }

  get tolerance() {
    let tolerance = this.getAttribute("tolerance")

    if (tolerance == null) {
      return DEFAULT_TOLERANCE
    }

    return parseInt(tolerance, 10)
  }
}

// This happens synchronously after turbo does its rendering, which is necessary
// to prevent a flash of the initial rendering. The web component uses
// requestAnimationFrame to invoke the parsedCallback, which sometimes causes a
// delay in determining the scroll position. - Aaron, Thu Jun 27 2024
window.addEventListener("turbo:render", () => {
  const elements = document.querySelectorAll("scroll-indicator")

  for (const element of elements) {
    if (element.visible === false || !element.checkVisibility()) {
      continue
    }

    element.displayIndicators()
  }
})

if (!window.customElements.get("scroll-indicator")) {
  window.ScrollIndicator = ScrollIndicator
  window.customElements.define("scroll-indicator", ScrollIndicator)
}
